import { useMemo } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';
import useNhStyleFingerprintSnapshot from '../../hooks/useNhStyleFingerprintSnapshot';
import {
  findIdentity,
  formatBreadth,
} from '../../utils/nhStyleFingerprintSnapshot';
import NhStyleFingerprintCoverageNote from './NhStyleFingerprintCoverageNote';
import NhStyleFingerprintHoverInfo from './NhStyleFingerprintHoverInfo';
import NhStyleFingerprintLinks from './NhStyleFingerprintLinks';
import NhStyleFingerprintMatches from './NhStyleFingerprintMatches';
import NhStyleFingerprintStatus from './NhStyleFingerprintStatus';
import NhStyleFingerprintStyleBars from './NhStyleFingerprintStyleBars';

function ProfileCounts({ identity }) {
  return (
    <Box sx={{ my: 1 }}>
      <Typography variant="body2">Catalog status: {identity.catalogStatus}</Typography>
      <Typography variant="body2">Catalog note: {identity.catalogNote}</Typography>
      <Typography variant="body2">Beer count: {identity.beerCount}</Typography>
      <Typography variant="body2">Mapped beers: {identity.mappedBeers}</Typography>
      <Typography variant="body2">Unmapped beers: {identity.unmappedBeers}</Typography>
      <Typography variant="body2">Member count: {identity.memberCount}</Typography>
      <Typography variant="body2">
        Out-of-business member count: {identity.outOfBusinessMemberCount}
      </Typography>
    </Box>
  );
}

function NhStyleFingerprintProfile({ identity, identities }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/nh-style-fingerprints">
          All identities
        </Link>
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="h5" component="h1">
          {identity.displayName}
        </Typography>
        <NhStyleFingerprintHoverInfo
          ariaLabel={`About ${identity.displayName}`}
          popupLabel="Identity details"
        >
          <ProfileCounts identity={identity} />
          <NhStyleFingerprintCoverageNote identity={identity} />
        </NhStyleFingerprintHoverInfo>
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {identity.kind}
      </Typography>
      <NhStyleFingerprintStyleBars identity={identity} />
      <Typography variant="body2">{formatBreadth(identity.breadth)}</Typography>
      <NhStyleFingerprintMatches identity={identity} identities={identities} />
      <NhStyleFingerprintLinks identity={identity} />
    </Box>
  );
}

function MissingIdentity() {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        This identity is not in the loaded snapshot.
      </Typography>
      <Link component={RouterLink} to="/nh-style-fingerprints">
        All identities
      </Link>
    </Box>
  );
}

function NhStyleFingerprintProfilePage() {
  const { identityId } = useParams();
  const { snapshot, loading, error } = useNhStyleFingerprintSnapshot();
  const identity = useMemo(
    () => findIdentity(snapshot, identityId ?? ''),
    [identityId, snapshot],
  );

  if (loading || error || !snapshot) {
    return (
      <NhStyleFingerprintStatus loading={loading} error={error} snapshot={snapshot} />
    );
  }

  if (!identity) {
    return <MissingIdentity />;
  }

  const identities = Array.isArray(snapshot.identities) ? snapshot.identities : [];
  return <NhStyleFingerprintProfile identity={identity} identities={identities} />;
}

export default NhStyleFingerprintProfilePage;
